import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  RefreshCw,
  MapPin,
  Clock,
  Car,
  CheckCircle,
  AlertCircle,
  LogIn,
  LogOut,
  ShieldCheck,
  ShieldAlert,
  Search,
  Phone,
  LogOut as SignOut,
} from "lucide-react";

const callNumber = (phone) => {
  if (!phone) return;
  window.location.href = `tel:${phone}`;
};

const STATUS = {
  reserved: { badge: "bg-blue-100 text-blue-800", label: "Reserved", icon: AlertCircle },
  checked_in: { badge: "bg-green-100 text-green-800", label: "Checked In", icon: CheckCircle },
  completed: { badge: "bg-gray-100 text-gray-700", label: "Completed", icon: CheckCircle },
  cancelled: { badge: "bg-red-100 text-red-800", label: "Cancelled", icon: AlertCircle },
};

const VERIFY = {
  verified: { badge: "bg-emerald-100 text-emerald-800", label: "Verified ✓" },
  mismatch: { badge: "bg-amber-100 text-amber-900", label: "Wrong spot ⚠" },
};

const AttendantPage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [plate, setPlate] = useState("");
  const [lookup, setLookup] = useState(null); // { plate, registered, matches }
  const [lookingUp, setLookingUp] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const localDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const doLookup = async (e) => {
    if (e) e.preventDefault();
    const q = plate.trim();
    if (!q) return;
    setLookingUp(true);
    setLookup(null);
    try {
      const res = await axios.get(
        `${API}/attendant/vehicles/lookup?plate=${encodeURIComponent(q)}&date=${localDate()}`
      );
      setLookup(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Lookup failed");
    } finally {
      setLookingUp(false);
    }
  };

  const raiseFlag = async (plateValue, bookingId = null) => {
    const note = window.prompt(
      "Reason for the flag (e.g. 'parked in A1 without booking'):",
      ""
    );
    if (note === null) return; // cancelled
    try {
      await axios.post(`${API}/attendant/flags`, {
        plate: plateValue,
        note,
        booking_id: bookingId,
        date: localDate(),
      });
      toast.success("Flag raised");
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to raise flag");
    }
  };

  const fetchBookings = async () => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/admin/bookings/today?date=${localDate()}`);
      setBookings(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load bookings");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    const interval = setInterval(fetchBookings, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (booking, kind) => {
    setBusyId(booking._id + kind);
    try {
      if (kind === "checkin") {
        await axios.post(`${API}/attendant/bookings/${booking._id}/checkin`);
        toast.success("Checked in on behalf of the rider");
      } else if (kind === "checkout") {
        await axios.post(`${API}/attendant/bookings/${booking._id}/checkout`);
        toast.success("Checked out");
      } else if (kind === "verify") {
        await axios.post(`${API}/attendant/bookings/${booking._id}/verify`, { status: "verified" });
        toast.success("Spot verified");
      } else if (kind === "mismatch") {
        const note = window.prompt("Describe the mismatch (e.g. 'car in slot B2 not A1'):", "");
        if (note === null) return; // cancelled
        await axios.post(`${API}/attendant/bookings/${booking._id}/verify`, { status: "mismatch", note });
        toast.success("Mismatch flagged");
      }
      await fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const signOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    navigate("/");
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bookings;
    return bookings.filter((b) => {
      const plate = b.vehicle?.license_plate?.toLowerCase() || "";
      const name = b.user_name?.toLowerCase() || "";
      const slot = String(b.slot_number || "").toLowerCase();
      return plate.includes(q) || name.includes(q) || slot.includes(q);
    });
  }, [bookings, search]);

  const counts = useMemo(() => {
    const c = { reserved: 0, checked_in: 0, completed: 0, mismatch: 0 };
    for (const b of bookings) {
      if (c[b.status] !== undefined) c[b.status] += 1;
      if (b.verification_status === "mismatch") c.mismatch += 1;
    }
    return c;
  }, [bookings]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#2D5F52] text-white">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Parking Attendant</h1>
            <p className="text-sm text-white/70">
              {user.name ? `${user.name} · ` : ""}Today's lot validation
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={fetchBookings} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="secondary" size="sm" onClick={signOut}>
              <SignOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Stat chips */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          <Card className="p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">{counts.reserved}</div>
            <div className="text-xs text-gray-500">Reserved</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-2xl font-bold text-green-700">{counts.checked_in}</div>
            <div className="text-xs text-gray-500">Checked In</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-2xl font-bold text-gray-700">{counts.completed}</div>
            <div className="text-xs text-gray-500">Completed</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-2xl font-bold text-amber-700">{counts.mismatch}</div>
            <div className="text-xs text-gray-500">Mismatches</div>
          </Card>
        </div>

        {/* Plate lookup — find any vehicle (even one with no booking) and flag it */}
        <Card className="p-4 mb-4">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700">
            <ShieldAlert className="w-4 h-4 text-amber-600" />
            Look up a vehicle by plate
          </div>
          <form onSubmit={doLookup} className="flex gap-2">
            <Input
              placeholder="Enter vehicle number…"
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
              className="uppercase"
            />
            <Button type="submit" disabled={lookingUp}>
              {lookingUp ? "Searching…" : "Search"}
            </Button>
          </form>

          {lookup && (
            <div className="mt-3">
              {!lookup.registered ? (
                <div className="flex flex-wrap items-center justify-between gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="text-sm text-red-800">
                    <strong>{lookup.plate}</strong> is not registered in this lot — likely unauthorized.
                  </div>
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => raiseFlag(lookup.plate)}
                  >
                    <ShieldAlert className="w-4 h-4 mr-1" /> Raise flag
                  </Button>
                </div>
              ) : (
                lookup.matches.map((m) => (
                  <div key={m.vehicle.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="text-sm text-gray-700 space-y-1">
                        <div>
                          <span className="font-mono bg-gray-200 px-1.5 py-0.5 rounded">{m.vehicle.license_plate}</span>{" "}
                          {m.vehicle.make} {m.vehicle.model} · {m.owner?.name || "Unknown owner"}
                        </div>
                        {m.booking ? (
                          <div className="text-gray-600">
                            Booking: <Badge className={(STATUS[m.booking.status] || STATUS.reserved).badge}>
                              {(STATUS[m.booking.status] || STATUS.reserved).label}
                            </Badge>{" "}
                            · Slot {m.booking.slot_number || "—"} · {m.booking.time_slot?.name || "—"}
                          </div>
                        ) : (
                          <div className="text-amber-700 font-medium">No booking today — parked without a reservation.</div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={() => raiseFlag(m.vehicle.license_plate, m.booking?.id || null)}
                        >
                          <ShieldAlert className="w-4 h-4 mr-1" /> Raise flag
                        </Button>
                        {m.owner?.phone && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => callNumber(m.owner.phone)}
                          >
                            <Phone className="w-4 h-4 mr-1" /> Call owner
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Card>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search by plate, name, or slot…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-500">No bookings found.</div>
        ) : (
          <div className="space-y-3">
            {filtered.map((b) => {
              const st = STATUS[b.status] || STATUS.reserved;
              const vf = b.verification_status ? VERIFY[b.verification_status] : null;
              return (
                <Card key={b._id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{b.user_name || "Unknown"}</span>
                        <Badge className={st.badge}>{st.label}</Badge>
                        {vf && <Badge className={vf.badge}>{vf.label}</Badge>}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Car className="w-4 h-4" />
                        {b.vehicle?.make} {b.vehicle?.model}
                        <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                          {b.vehicle?.license_plate || "—"}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" /> Slot {b.slot_number || "—"}
                          {b.floor ? ` · Floor ${b.floor}` : ""}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" /> {b.time_slot?.name || "—"}
                        </span>
                      </div>
                      {b.verification_note && (
                        <div className="text-xs text-amber-700">Note: {b.verification_note}</div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {b.status === "reserved" && (
                        <Button
                          size="sm"
                          onClick={() => act(b, "checkin")}
                          disabled={busyId === b._id + "checkin"}
                        >
                          <LogIn className="w-4 h-4 mr-1" /> Check in
                        </Button>
                      )}
                      {b.status === "checked_in" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => act(b, "checkout")}
                          disabled={busyId === b._id + "checkout"}
                        >
                          <LogOut className="w-4 h-4 mr-1" /> Check out
                        </Button>
                      )}
                      {b.status !== "cancelled" && b.status !== "completed" && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => act(b, "verify")}
                            disabled={busyId === b._id + "verify"}
                          >
                            <ShieldCheck className="w-4 h-4 mr-1" /> Verify
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-amber-700 hover:text-amber-900"
                            onClick={() => act(b, "mismatch")}
                            disabled={busyId === b._id + "mismatch"}
                          >
                            <ShieldAlert className="w-4 h-4 mr-1" /> Flag
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendantPage;
